import { useEffect, useRef } from 'react';
import { Application, Assets, Sprite, type Texture } from 'pixi.js';

const LEAF_FRAMES = [
  '/leaves/leaf-russet.png',
  '/leaves/leaf-gold.png',
  '/leaves/leaf-ochre.png',
];

/** Number of leaves drifting across the map at any given time. Picked by
 *  feel: at LEAF_SCALE 0.5 the sprites are small enough that ~30 reads as
 *  "breeze full of leaves" rather than "swarm". */
const LEAF_COUNT = 30;

/** Native leaf sprite size, in map coords. The PNGs are 16×16; we render
 *  at half that — the leaves on the parchment houses below are small, so
 *  airborne leaves at native size read as oversized. */
const LEAF_SIZE = 16;
const LEAF_SCALE = 0.5;

/** Native dimensions of the day/night map images in app/public/. */
const MAP_W = 1536;
const MAP_H = 1024;

/** Shoreline frames are 1536×294 strips; the top of the strip lines up
 *  with y=730 in the map image (1024 − 294). */
const SHORELINE_TOP_IN_MAP = MAP_H - 294;

const SHORELINE_FRAMES = [
  '/shoreline/wave0.png',
  '/shoreline/wave1.png',
  '/shoreline/wave2.png',
  '/shoreline/wave3.png',
];

/** ms per frame. Ping-pongs 0→1→2→3→2→1→0 so the wave that recedes also
 *  rolls back in instead of snap-cutting to frame 0. 1500 ms × 6 transitions
 *  per cycle = 9 s of slow, breathing lapping. */
const SHORELINE_FRAME_MS = 1500;

/** Tree-sway frames are 1536×912 — the upper portion of the map, with the
 *  canopies redrawn in five slightly nudged poses. Anchored to the top of
 *  the map; the bottom of the strip stops at y=912, just inside the
 *  shoreline strip's top edge so the shoreline waves keep painting over it. */
const TREES_FRAMES = [
  '/trees/trees0.png',
  '/trees/trees1.png',
  '/trees/trees2.png',
  '/trees/trees3.png',
  '/trees/trees4.png',
];

/** ms per frame. 5 frames ping-ponged = 8 transitions per cycle; at 900 ms
 *  that's a ~7.2 s sway — a slow, breathy drift rather than a brisk shake,
 *  which sells "still autumn afternoon" rather than "windy". */
const TREES_FRAME_MS = 900;

/** PixiJS scene that lives over the map. Today it animates the shoreline;
 *  it's set up as a single Application so additional sprite layers (tree
 *  sway, lantern flicker, etc.) can be added alongside without spinning up
 *  another canvas. */
export function MapAnimations() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    let cancelled = false;
    let app: Application | null = null;
    let raf = 0;

    const onWindowResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (cancelled || !app) return;
        layoutAll();
      });
    };

    // Forward declarations so the resize handler can call into the layout
    // closures we set up after the async load completes.
    const layoutFns: Array<() => void> = [];
    const layoutAll = () => layoutFns.forEach(fn => fn());

    async function setup() {
      const application = new Application();
      await application.init({
        resizeTo: node!,
        backgroundAlpha: 0,
        antialias: false,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      if (cancelled) {
        application.destroy(true);
        return;
      }
      app = application;
      const canvas = application.canvas;
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.imageRendering = 'pixelated';
      node!.appendChild(canvas);

      /** Returns the same rect that `background-size: contain; background-position: center`
       *  lays the map image out at, so sprites overlaid here line up with
       *  the underlying CSS-painted background. */
      const mapRect = () => {
        const w = application.screen.width;
        const h = application.screen.height;
        const scale = Math.min(w / MAP_W, h / MAP_H);
        return {
          scale,
          offsetX: (w - MAP_W * scale) / 2,
          offsetY: (h - MAP_H * scale) / 2,
        };
      };

      // Trees first so the shoreline paints on top of the strip's bottom
      // edge — otherwise the static water pixels in the tree frames would
      // freeze the lapping animation in their overlap zone.
      await mountTrees(application, mapRect, layoutFns, () => cancelled);
      await mountShoreline(application, mapRect, layoutFns, () => cancelled);
      await mountLeaves(application, mapRect, layoutFns, () => cancelled);

      layoutAll();
      window.addEventListener('resize', onWindowResize);
    }

    setup();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onWindowResize);
      if (app) {
        app.destroy(true, { children: true, texture: false });
        app = null;
      }
    };
  }, []);

  return (
    <div
      className="map-view__animations"
      ref={containerRef}
      aria-hidden="true"
    />
  );
}

async function mountShoreline(
  app: Application,
  mapRect: () => { scale: number; offsetX: number; offsetY: number },
  layoutFns: Array<() => void>,
  isCancelled: () => boolean,
) {
  const textures = await Promise.all(
    SHORELINE_FRAMES.map(p => Assets.load<Texture>(p)),
  );
  if (isCancelled()) return;

  // Pixel-art textures must use nearest-neighbor or they go to mush at
  // non-integer scales (which `cover` always produces).
  textures.forEach(t => {
    t.source.scaleMode = 'nearest';
  });

  const sprite = new Sprite(textures[0]);
  sprite.anchor.set(0.5, 0); // top-center; we position by the strip's top edge
  app.stage.addChild(sprite);

  // Manual frame stepping so we can ping-pong (PixiJS's AnimatedSprite only
  // does forward/backward loops, not a bounce between endpoints).
  let frameIdx = 0;
  let dir = 1;
  let elapsedMs = 0;
  app.ticker.add(ticker => {
    elapsedMs += ticker.deltaMS;
    if (elapsedMs < SHORELINE_FRAME_MS) return;
    elapsedMs -= SHORELINE_FRAME_MS;
    frameIdx += dir;
    if (frameIdx >= textures.length - 1) {
      frameIdx = textures.length - 1;
      dir = -1;
    } else if (frameIdx <= 0) {
      frameIdx = 0;
      dir = 1;
    }
    sprite.texture = textures[frameIdx];
  });

  const layout = () => {
    const { scale, offsetX, offsetY } = mapRect();
    sprite.scale.set(scale);
    sprite.x = offsetX + (MAP_W * scale) / 2;
    sprite.y = offsetY + SHORELINE_TOP_IN_MAP * scale;
  };
  layoutFns.push(layout);
}

async function mountTrees(
  app: Application,
  mapRect: () => { scale: number; offsetX: number; offsetY: number },
  layoutFns: Array<() => void>,
  isCancelled: () => boolean,
) {
  const textures = await Promise.all(
    TREES_FRAMES.map(p => Assets.load<Texture>(p)),
  );
  if (isCancelled()) return;

  textures.forEach(t => {
    t.source.scaleMode = 'nearest';
  });

  const sprite = new Sprite(textures[0]);
  sprite.anchor.set(0, 0); // top-left; the strip starts at the map's top-left
  app.stage.addChild(sprite);

  // Same ping-pong stepping pattern as the shoreline so the canopies sway
  // back and forth rather than snapping from end-pose to start-pose.
  let frameIdx = 0;
  let dir = 1;
  let elapsedMs = 0;
  app.ticker.add(ticker => {
    elapsedMs += ticker.deltaMS;
    if (elapsedMs < TREES_FRAME_MS) return;
    elapsedMs -= TREES_FRAME_MS;
    frameIdx += dir;
    if (frameIdx >= textures.length - 1) {
      frameIdx = textures.length - 1;
      dir = -1;
    } else if (frameIdx <= 0) {
      frameIdx = 0;
      dir = 1;
    }
    sprite.texture = textures[frameIdx];
  });

  const layout = () => {
    const { scale, offsetX, offsetY } = mapRect();
    sprite.scale.set(scale);
    sprite.x = offsetX;
    sprite.y = offsetY;
  };
  layoutFns.push(layout);
}

/** Each leaf carries its own drift physics expressed in *map coordinates*
 *  (the unscaled 1536×1024 grid). The layout pass converts to screen pixels,
 *  so leaves keep their relative pace whether the map is shown at 0.6× or
 *  1.4×.
 *
 *  Motion model: a slow rightward wind, a sine-wave vertical bob, and a
 *  lazy rotation. None of these are tuned for realism — they're tuned for
 *  "gentle autumn afternoon": speeds low enough that the eye isn't pulled
 *  away from the town below. */
type Leaf = {
  sprite: Sprite;
  /** Map-space x, can drift past MAP_W; recycled to the left edge on exit. */
  mapX: number;
  /** Map-space y baseline (the centre of the bob). */
  mapBaseY: number;
  /** Horizontal drift, map px / sec. ~20-50 = a breath of wind, not a gust. */
  speedX: number;
  /** Bob amplitude in map px. */
  bobAmp: number;
  /** Bob period in seconds; randomised per-leaf so the flock never marches in lockstep. */
  bobPeriod: number;
  /** Phase offset so leaves don't all peak together. */
  bobPhase: number;
  /** Rotation in radians per second; sign randomised so some spin each way. */
  rotSpeed: number;
  /** Cumulative seconds, owned per-leaf so a delta-time pause doesn't desync the flock. */
  age: number;
};

async function mountLeaves(
  app: Application,
  mapRect: () => { scale: number; offsetX: number; offsetY: number },
  layoutFns: Array<() => void>,
  isCancelled: () => boolean,
) {
  const textures = await Promise.all(
    LEAF_FRAMES.map(p => Assets.load<Texture>(p)),
  );
  if (isCancelled()) return;

  textures.forEach(t => {
    t.source.scaleMode = 'nearest';
  });

  // Cosy band: leaves drift through the upper two-thirds of the map (sky +
  // canopies + village rooftops) and stop short of the shoreline so they
  // don't visibly skim the water.
  const Y_MIN = 40;
  const Y_MAX = MAP_H - 360;

  const rand = (min: number, max: number) => min + Math.random() * (max - min);

  const leaves: Leaf[] = Array.from({ length: LEAF_COUNT }, () => {
    const sprite = new Sprite(textures[Math.floor(Math.random() * textures.length)]);
    sprite.anchor.set(0.5);
    app.stage.addChild(sprite);
    return {
      sprite,
      // Stagger initial x across the full map so they don't all enter at once on mount.
      mapX: rand(-LEAF_SIZE, MAP_W),
      mapBaseY: rand(Y_MIN, Y_MAX),
      speedX: rand(20, 50),
      bobAmp: rand(6, 18),
      bobPeriod: rand(2.4, 4.2),
      bobPhase: rand(0, Math.PI * 2),
      rotSpeed: rand(-1.2, 1.2),
      age: rand(0, 6),
    };
  });

  /** Cached layout values; the ticker reads these every frame, the layout
   *  callback rewrites them on resize. Keeping them in closure scope avoids
   *  recomputing mapRect() 14× per frame. */
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;

  app.ticker.add(ticker => {
    const dt = ticker.deltaMS / 1000;
    for (const leaf of leaves) {
      leaf.age += dt;
      leaf.mapX += leaf.speedX * dt;
      // Recycle off the right edge back to just outside the left so the
      // wind looks like a continuous stream rather than a one-shot pass.
      if (leaf.mapX > MAP_W + LEAF_SIZE) {
        leaf.mapX = -LEAF_SIZE;
        leaf.mapBaseY = rand(Y_MIN, Y_MAX);
        leaf.sprite.texture =
          textures[Math.floor(Math.random() * textures.length)];
      }
      const bobY =
        leaf.mapBaseY +
        Math.sin((leaf.age / leaf.bobPeriod) * Math.PI * 2 + leaf.bobPhase) *
          leaf.bobAmp;
      leaf.sprite.x = offsetX + leaf.mapX * scale;
      leaf.sprite.y = offsetY + bobY * scale;
      leaf.sprite.rotation += leaf.rotSpeed * dt;
    }
  });

  const layout = () => {
    const r = mapRect();
    scale = r.scale;
    offsetX = r.offsetX;
    offsetY = r.offsetY;
    // Sprite scale only changes on resize, not every frame — set it here.
    for (const leaf of leaves) {
      leaf.sprite.scale.set(scale * LEAF_SCALE);
    }
  };
  layoutFns.push(layout);
}
